import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Calendar,
  Clock,
  MapPin,
  Video,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import { PageHero } from "@/components/Section";
import { api } from "@/lib/api";
import useReveal from "@/hooks/useReveal";
import { church, fullAddress } from "@/config/church";
import StockPhotoNotice from "@/components/StockPhotoNotice";

const FAMILY_DAY = {
  eyebrow: "Special Sunday",
  title: "Friends & Family Day",
  date: "Sunday, October 11, 2026",
  time: "11:00 AM Worship Service",
  location: "Main Sanctuary",
  description:
    "Bring your family. Invite your friends. A day of worship, fellowship, a shared meal, door prizes, and giveaways. Largest family group wins $100.",
  image:
    "https://customer-assets-eiarnc6j.emergentagent.net/job_strange-cartwright-8/artifacts/bf7g84zx_IMG_5577.jpg",
  cta: { label: "RSVP Now", to: "/family-day" },
};

export default function Events() {
  useReveal();
  const [gatherings, setGatherings] = useState([]);

  useEffect(() => {
    api
      .get("/events")
      .then((r) => setGatherings(r.data || []))
      .catch(() => {});
  }, []);

  return (
    <div data-testid="events-page">
      <PageHero
        eyebrow="Events & Gatherings"
        title="Where God's family gathers."
        subtitle="Come worship, study, and grow with us — every week and for every season. A seat is waiting for you."
        image="https://customer-assets-eiarnc6j.emergentagent.net/job_strange-cartwright-8/artifacts/bf7g84zx_IMG_5577.jpg"
      />

      {/* SPECIAL EVENT — Friends & Family Day */}
      <section className="bg-white py-20 md:py-24">
        <div className="container-npbc">
          <div className="flex items-end gap-3 mb-8" data-reveal>
            <Sparkles className="h-5 w-5 text-npbc-gold" />
            <div className="text-[10px] font-bold uppercase tracking-[0.28em] text-npbc-red">
              Coming Up
            </div>
          </div>

          <article
            data-reveal
            data-testid="events-special-family-day"
            className="grid gap-0 md:grid-cols-[1.1fr_1fr] rounded-2xl overflow-hidden bg-npbc-black text-white shadow-[0_20px_60px_-20px_rgba(0,0,0,0.4)]"
          >
            <div className="relative min-h-[280px] md:min-h-[420px]">
              <img
                src={FAMILY_DAY.image}
                alt={FAMILY_DAY.title}
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-npbc-black/85 via-npbc-black/20 to-transparent md:bg-gradient-to-r md:from-transparent md:via-npbc-black/20 md:to-npbc-black/50" />
              <div className="absolute top-4 left-4 rounded-full bg-npbc-gold text-npbc-black px-3 py-1 text-[10px] font-bold uppercase tracking-[0.22em]">
                {FAMILY_DAY.eyebrow}
              </div>
            </div>
            <div className="p-8 md:p-12 flex flex-col justify-center">
              <h2 className="heading-serif text-4xl md:text-5xl leading-[1.05]">
                {FAMILY_DAY.title}
              </h2>
              <div className="mt-5 space-y-3 text-sm">
                <div className="flex items-center gap-3 text-white/85">
                  <Calendar className="h-4 w-4 text-npbc-gold" />
                  <span>{FAMILY_DAY.date}</span>
                </div>
                <div className="flex items-center gap-3 text-white/85">
                  <Clock className="h-4 w-4 text-npbc-gold" />
                  <span>{FAMILY_DAY.time}</span>
                </div>
                <div className="flex items-center gap-3 text-white/85">
                  <MapPin className="h-4 w-4 text-npbc-gold" />
                  <span>{FAMILY_DAY.location}</span>
                </div>
              </div>
              <p className="mt-5 text-white/75 leading-relaxed text-sm">
                {FAMILY_DAY.description}
              </p>
              <Link
                to={FAMILY_DAY.cta.to}
                data-testid="events-family-day-rsvp"
                className="btn-gold mt-8 self-start"
              >
                {FAMILY_DAY.cta.label}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </article>
        </div>
      </section>

      {/* WEEKLY GATHERINGS */}
      <section className="bg-npbc-cream py-20 md:py-24">
        <div className="container-npbc">
          <div className="mb-10" data-reveal>
            <div className="eyebrow">Every Week</div>
            <h2 className="mt-3 heading-serif text-4xl md:text-5xl leading-[1.05]">
              Weekly gatherings.
            </h2>
            <p className="mt-4 text-npbc-gray/85 max-w-2xl">
              Our rhythm of worship, study, and fellowship — the same time, every week. Come as often as you can.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {gatherings.length === 0 ? (
              <div className="col-span-full text-center text-npbc-gray/60 py-12">
                Loading schedule...
              </div>
            ) : (
              gatherings.map((g, i) => (
                <article
                  key={g.id}
                  data-reveal
                  data-testid={`events-gathering-${g.id}`}
                  style={{ transitionDelay: `${i * 60}ms` }}
                  className="rounded-2xl bg-white border border-black/[0.06] overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.04),0_20px_60px_-30px_rgba(0,0,0,0.15)] hover:-translate-y-1 hover:shadow-[0_1px_3px_rgba(0,0,0,0.05),0_36px_80px_-30px_rgba(0,0,0,0.35)] transition-all duration-500"
                >
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={g.image}
                      alt={g.title}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                    <div className="absolute top-3 left-3 rounded-full bg-white/95 backdrop-blur px-3 py-1 text-[10px] font-bold uppercase tracking-[0.22em] text-npbc-red">
                      {g.category}
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="heading-serif text-2xl leading-snug">{g.title}</h3>
                    <div className="mt-3 space-y-1.5 text-sm text-npbc-gray/85">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-3.5 w-3.5 text-npbc-red" />
                        <span>{g.date}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="h-3.5 w-3.5 text-npbc-red" />
                        <span>{g.time}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin className="h-3.5 w-3.5 text-npbc-red" />
                        <span>{g.location}</span>
                      </div>
                    </div>
                    <p className="mt-4 text-sm text-npbc-gray/80 leading-relaxed">
                      {g.description}
                    </p>
                    {g.zoom_url && (
                      <a
                        href={g.zoom_url}
                        target="_blank"
                        rel="noreferrer noopener"
                        data-testid={`events-${g.id}-zoom-btn`}
                        className="mt-5 inline-flex items-center gap-2 rounded-full bg-npbc-gold px-4 py-2 text-xs font-bold text-npbc-black hover:brightness-95 transition"
                      >
                        <Video className="h-3.5 w-3.5" />
                        Join on Zoom
                      </a>
                    )}
                  </div>
                </article>
              ))
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-npbc-black text-white py-20">
        <div className="container-npbc text-center max-w-2xl mx-auto" data-reveal>
          <div className="eyebrow !text-npbc-gold justify-center flex">Come as You Are</div>
          <h2 className="mt-3 heading-serif text-4xl md:text-5xl">Your first Sunday is on us.</h2>
          <p className="mt-4 text-white/75">
            Fill out our short guest form and we'll have someone greet you at the door.
          </p>
          <Link to="/visit" className="btn-gold mt-8">
            Plan Your Visit <ArrowRight className="h-4 w-4" />
          </Link>
          <p className="mt-8 text-xs uppercase tracking-[0.24em] text-white/50">
            {fullAddress} · {church.phone}
          </p>
        </div>
      </section>
      <StockPhotoNotice />
    </div>
  );
}
